import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  FaChartPie, FaTshirt, FaBoxes, FaReceipt, FaTags, FaCog, FaSignOutAlt,
  FaBars, FaTimes, FaExternalLinkAlt, FaFlask, FaHistory, FaUsers, FaUserFriends,
  FaFileInvoiceDollar, FaAddressBook,
} from 'react-icons/fa'
import { useAuth } from '../context/AuthContext'
import { DEMO_NOTICE } from '../lib/demo'
import { SHOW_STOCK } from '../lib/features'

/**
 * Sidebar, top bar and the demo banner.
 *
 * The navy sidebar is the one place the brand colour carries real weight. It is
 * chrome, so the contrast requirements are WCAG text contrast against white
 * rather than the chart-palette checks — #2c3e50 against white is 10.4:1, which
 * is comfortably AAA.
 */

// `show` defaults to true; an entry that names a switched-off feature drops out
// of the sidebar without leaving a gap. See lib/features.js.
const NAV = [
  { to: '/', label: 'Dashboard', icon: FaChartPie, end: true },
  { to: '/products', label: 'Products', icon: FaTshirt },
  { to: '/inventory', label: 'Inventory', icon: FaBoxes, show: SHOW_STOCK },
  { to: '/orders', label: 'Orders', icon: FaReceipt },
  { to: '/customers', label: 'Customers', icon: FaUserFriends },
  // Invoicing sits next to Customers rather than under Settings: making one is
  // daily counter work, not configuration.
  { to: '/invoices', label: 'Invoices', icon: FaFileInvoiceDollar },
  { to: '/suppliers', label: 'Suppliers', icon: FaAddressBook },
  { to: '/analytics', label: 'Analytics', icon: FaChartPie },
  { to: '/categories', label: 'Categories', icon: FaTags },
  { to: '/activity', label: 'Activity', icon: FaHistory },
  { to: '/team', label: 'Team', icon: FaUsers },
  { to: '/settings', label: 'Settings', icon: FaCog },
]

const STOREFRONT_URL = import.meta.env.VITE_STOREFRONT_URL || 'http://localhost:5173'

const AppShell = ({ children }) => {
  const { profile, signOut, isDemo, role } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  // Close the drawer on navigation, or it stays over the page the user just
  // asked for on a phone.
  React.useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const initials = (profile?.full_name || profile?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  const sidebar = (
    <div className="flex h-full flex-col bg-brand-navy text-white">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 font-heading text-base font-bold">
          C
        </span>
        <div className="min-w-0">
          <div className="font-heading text-sm font-bold leading-tight">Coquis</div>
          <div className="text-2xs text-white/55">Store manager</div>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-4">
        {NAV.filter(({ show }) => show ?? true).map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${
                isActive ? 'bg-white/15 text-white' : 'text-white/65 hover:bg-white/8 hover:text-white'
              }`
            }
          >
            <Icon className="shrink-0 text-[13px]" aria-hidden="true" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-2.5">
        <a
          href={STOREFRONT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-2xs text-white/60 transition-colors hover:bg-white/8 hover:text-white"
        >
          <FaExternalLinkAlt className="shrink-0 text-[10px]" aria-hidden="true" />
          View the shop
        </a>

        <div className="mt-1 flex items-center gap-2.5 px-3 py-2">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/15 text-2xs font-semibold">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-2xs font-medium">{profile?.full_name || profile?.email}</div>
            <div className="text-2xs capitalize text-white/50">{role}</div>
          </div>
          {!isDemo && (
            <button
              onClick={signOut}
              className="shrink-0 rounded p-1.5 text-white/55 transition-colors hover:bg-white/10 hover:text-white"
              title="Sign out"
              aria-label="Sign out"
            >
              <FaSignOutAlt className="text-[12px]" />
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen">
      {/* Demo mode is announced permanently and unmissably. A simulated revenue
          figure mistaken for a real one is worse than showing no figure. */}
      {isDemo && (
        <div className="sticky top-0 z-40 flex items-center justify-center gap-2 bg-status-warning px-4 py-1.5 text-center text-2xs font-semibold text-[#3d2c00]">
          <FaFlask className="shrink-0 text-[10px]" aria-hidden="true" />
          <span>{DEMO_NOTICE}</span>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="sticky top-0 hidden h-screen w-56 shrink-0 lg:block">{sidebar}</aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <button
              className="absolute inset-0 bg-black/45"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            />
            <div className="absolute inset-y-0 left-0 w-60 animate-slide-up">{sidebar}</div>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-hairline bg-surface/90 px-4 py-2.5 backdrop-blur-sm lg:hidden">
            <button
              onClick={() => setMobileOpen((open) => !open)}
              className="btn-subtle btn-sm"
              aria-label="Open menu"
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
            <span className="font-heading text-sm font-bold">Coquis</span>
          </header>

          <main className="mx-auto w-full max-w-[1400px] px-4 py-5 sm:px-6">{children}</main>
        </div>
      </div>
    </div>
  )
}

export default AppShell
